import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowUpRight, Download, ExternalLink, Mail, MapPin, Phone, X } from 'lucide-react'
import { CATEGORY_LABELS, type PortfolioPayload, type Project, type ProjectCategory, type ProjectGalleryItem } from '../types'

const categoryOrder: Array<'all' | ProjectCategory> = [
  'all',
  'game-interaction',
  'product-design',
  'student-project',
  'other',
]

function ProjectVisual({ project, index }: { project: Project; index: number }) {
  if (project.cover_url) {
    return <img src={project.cover_url} alt={project.cover_alt || project.title} loading="lazy" />
  }

  return (
    <div className={`project-placeholder tone-${(index % 4) + 1}`} aria-hidden="true">
      <span>{String(index + 1).padStart(2, '0')}</span>
      <div className="placeholder-lines"><i /><i /><i /></div>
      <strong>{project.title.slice(0, 2)}</strong>
    </div>
  )
}

function CaseStudyImage({ item, index }: { item: ProjectGalleryItem; index: number }) {
  const [failed, setFailed] = useState(false)

  return (
    <figure className="case-study-page">
      {failed ? (
        <div className="case-study-placeholder" role="img" aria-label={`${item.image_alt}，图片暂不可用`}>
          <span>IMAGE PLACEHOLDER</span>
          <strong>{String(index + 1).padStart(2, '0')}</strong>
          <p>原稿图片暂不可用，已保留版位与说明。</p>
        </div>
      ) : (
        <img
          src={item.image_url}
          alt={item.image_alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      {item.caption && <figcaption><span>{String(index + 1).padStart(2, '0')}</span>{item.caption}</figcaption>}
    </figure>
  )
}

function ProjectDetail({ project, onClose }: { project: Project; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    document.body.classList.add('modal-open')
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('modal-open')
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div className="project-detail" role="dialog" aria-modal="true" aria-label={project.title}>
      <header className="detail-nav">
        <button className="text-button" onClick={onClose}><ArrowLeft size={18} /> 返回作品集</button>
        <button className="icon-button" onClick={onClose} aria-label="关闭"><X size={20} /></button>
      </header>
      <div className="detail-hero">
        <div>
          <p className="eyebrow">{CATEGORY_LABELS[project.category]} · {project.period}</p>
          <h1>{project.title}</h1>
          <p className="detail-summary">{project.summary}</p>
        </div>
        <dl className="project-meta">
          <div><dt>我的角色</dt><dd>{project.role}</dd></div>
          <div><dt>项目时间</dt><dd>{project.period}</dd></div>
          <div><dt>关键词</dt><dd>{project.tags.join(' · ')}</dd></div>
        </dl>
      </div>
      <div className="detail-cover"><ProjectVisual project={project} index={0} /></div>
      <div className="detail-body">
        <p className="eyebrow">PROJECT STORY</p>
        <h2>从问题到体验</h2>
        {project.content.split('\n').filter(Boolean).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        {project.highlights.length > 0 && (
          <div className="outcomes">
            <p className="eyebrow">OUTCOMES</p>
            {project.highlights.map((item, index) => (
              <div key={item}><span>{String(index + 1).padStart(2, '0')}</span><strong>{item}</strong></div>
            ))}
          </div>
        )}
      </div>
      {project.gallery && project.gallery.length > 0 && (
        <section className="case-study-gallery" aria-label={`${project.title}案例原稿`}>
          <header>
            <div>
              <p className="eyebrow">DESIGN PROCESS</p>
              <h2>完整案例原稿</h2>
            </div>
            <p>依据原 PDF 顺序整理，共 {project.gallery.length} 页；图片按需加载。</p>
          </header>
          <div className="case-study-pages">
            {project.gallery.map((item, index) => <CaseStudyImage key={item.image_url} item={item} index={index} />)}
          </div>
        </section>
      )}
      <footer className="detail-footer">
        <span>感谢阅读</span>
        <button className="text-button" onClick={onClose}>浏览其他项目 <ArrowUpRight size={17} /></button>
      </footer>
    </div>
  )
}

export function PortfolioSite({ data, isDemo }: { data: PortfolioPayload; isDemo: boolean }) {
  type SiteView = 'home' | 'work' | 'resume'
  const readView = (): SiteView => {
    const hash = window.location.hash.replace('#', '')
    return hash === 'work' || hash === 'resume' ? hash : 'home'
  }

  const [view, setViewState] = useState<SiteView>(readView)
  const [filter, setFilter] = useState<'all' | ProjectCategory>('all')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const { profile, projects, access } = data

  useEffect(() => {
    const onHashChange = () => setViewState(readView())
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const setView = (next: SiteView) => {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${next}`)
    setViewState(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const visibleCategories = useMemo(
    () => categoryOrder.filter((category) => category === 'all' || projects.some((project) => project.category === category)),
    [projects],
  )
  const filteredProjects = filter === 'all' ? projects : projects.filter((project) => project.category === filter)
  const capabilities = profile.skills?.length
    ? profile.skills
    : Array.from(new Set(projects.flatMap((project) => project.tags))).slice(0, 12)

  return (
    <>
      {isDemo && <div className="demo-banner">本地预览模式 · 连接 Supabase 后启用后台管理与在线更新</div>}
      <div className="site-shell">
        <nav className="top-nav" aria-label="主导航">
          <button className="brand" onClick={() => setView('home')} aria-label="返回首页"><span>{profile.initials}</span></button>
          <div className="nav-tabs" role="tablist" aria-label="页面切换">
            <button role="tab" aria-selected={view === 'home'} className={view === 'home' ? 'active' : ''} onClick={() => setView('home')}>首页</button>
            <button role="tab" aria-selected={view === 'work'} className={view === 'work' ? 'active' : ''} onClick={() => setView('work')}>项目作品</button>
            <button role="tab" aria-selected={view === 'resume'} className={view === 'resume' ? 'active' : ''} onClick={() => setView('resume')}>个人简历</button>
          </div>
          <a className="contact-link" href={`mailto:${profile.email}`}>联系我 <ArrowUpRight size={16} /></a>
        </nav>

        <main className="view-stage">
          {view === 'home' && <section className="hero-section tab-page" aria-label="首页">
            <div className="hero-index"><span>PORTFOLIO</span><span>2026</span></div>
            <div className="hero-copy">
              <p className="eyebrow">INTERACTION / PRODUCT / EXPERIENCE</p>
              <h1>让复杂的<br /><em>体验</em>变得自然。</h1>
            </div>
            <div className="hero-bottom">
              <p>{profile.bio}</p>
              <button className="scroll-cue" onClick={() => setView('work')}>查看项目作品 <ArrowUpRight size={18} /></button>
            </div>
          </section>}

          {view === 'work' && <section className="work-section tab-page" aria-label="项目作品">
            <header className="section-heading">
              <div><p className="eyebrow">SELECTED WORK</p><h2>项目作品</h2></div>
              <span>{String(filteredProjects.length).padStart(2, '0')} PROJECTS</span>
            </header>
            <div className="category-tabs" role="tablist" aria-label="项目分类">
              {visibleCategories.map((category) => (
                <button
                  key={category}
                  className={filter === category ? 'active' : ''}
                  onClick={() => setFilter(category)}
                  role="tab"
                  aria-selected={filter === category}
                >
                  {category === 'all' ? '全部' : CATEGORY_LABELS[category]}
                </button>
              ))}
            </div>
            <div className="project-grid">
              {filteredProjects.map((project, index) => (
                <article className="project-card" key={project.id}>
                  <button className="project-cover" onClick={() => setSelectedProject(project)} aria-label={`查看 ${project.title}`}>
                    <ProjectVisual project={project} index={index} />
                    <span className="open-project"><ArrowUpRight size={19} /></span>
                  </button>
                  <div className="project-info">
                    <div><p className="project-category">{CATEGORY_LABELS[project.category]} · {project.period}</p><h3>{project.title}</h3></div>
                    <p>{project.summary}</p>
                  </div>
                  <div className="tag-row">{project.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                </article>
              ))}
            </div>
          </section>}

          {view === 'resume' && <section className="resume-page tab-page" aria-label="个人简历">
            <header className="resume-header">
              <div>
                <p className="eyebrow">CURRICULUM VITAE</p>
                <h1>{profile.name}</h1>
                <p>{profile.headline}</p>
              </div>
              {profile.resume_url
                ? <a className="primary-button" href={profile.resume_url} target="_blank" rel="noreferrer"><Download size={17} /> 下载 PDF 简历</a>
                : <span className="resume-note">可在管理端上传完整 PDF 简历</span>}
            </header>

            <div className="resume-layout">
              <aside className="resume-aside">
                <div className="resume-avatar">{profile.initials}</div>
                <section>
                  <p className="eyebrow">CONTACT</p>
                  <div className="about-meta">
                    <span><MapPin size={16} /> {profile.location}</span>
                    <span><Mail size={16} /> {profile.email}</span>
                    {profile.phone && <span><Phone size={16} /> {profile.phone}</span>}
                  </div>
                </section>
                <section>
                  <p className="eyebrow">STATUS</p>
                  <p>{profile.availability}</p>
                </section>
                {profile.portfolio_links && profile.portfolio_links.length > 0 && <section>
                  <p className="eyebrow">PORTFOLIO</p>
                  <div className="resume-links">
                    {profile.portfolio_links.map((link) => <a key={link.url} href={link.url} target="_blank" rel="noreferrer">{link.label}<ExternalLink size={14} /></a>)}
                  </div>
                </section>}
                <div className="stat-grid">
                  <div><strong>{profile.years_experience || '—'}</strong><span>年设计经验</span></div>
                  <div><strong>{profile.project_count || projects.length}</strong><span>个代表项目</span></div>
                </div>
              </aside>

              <div className="resume-main">
                <section className="resume-block">
                  <p className="eyebrow">PROFILE</p>
                  <h2>个人简介</h2>
                  <p className="resume-lead">{profile.bio}</p>
                </section>
                <section className="resume-block">
                  <p className="eyebrow">SELECTED EXPERIENCE</p>
                  <h2>工作经历</h2>
                  {profile.experiences?.length ? <div className="experience-list">
                    {profile.experiences.map((experience) => (
                      <article key={`${experience.company}-${experience.period}`}>
                        <header><span>{experience.period}</span><div><h3>{experience.company}</h3><strong>{experience.title}</strong>{experience.project && <small>{experience.project}</small>}</div></header>
                        <ul>{experience.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>
                      </article>
                    ))}
                  </div> : <div className="resume-timeline">
                    {projects.map((project) => <button key={project.id} onClick={() => setSelectedProject(project)}><span>{project.period}</span><div><small>{CATEGORY_LABELS[project.category]} · {project.role}</small><h3>{project.title}</h3><p>{project.summary}</p></div><ArrowUpRight size={18} /></button>)}
                  </div>}
                </section>
                {profile.education && profile.education.length > 0 && <section className="resume-block">
                  <p className="eyebrow">EDUCATION</p>
                  <h2>教育经历</h2>
                  <div className="education-list">
                    {profile.education.map((item) => <article key={`${item.school}-${item.period}`}><span>{item.period}</span><div><h3>{item.school}</h3><strong>{item.degree}</strong><small>{item.department}</small></div></article>)}
                  </div>
                </section>
                }
                <section className="resume-block">
                  <p className="eyebrow">CAPABILITIES</p>
                  <h2>专业方向</h2>
                  <div className="capability-list">{capabilities.map((item) => <span key={item}>{item}</span>)}</div>
                </section>
              </div>
            </div>
          </section>}
        </main>

        <footer className="compact-footer">
          <span>© {new Date().getFullYear()} {profile.name}</span>
          <span>{access?.expires_at ? `本链接有效至 ${new Date(access.expires_at).toLocaleString('zh-CN')}` : profile.availability}</span>
          <a href="/admin">管理入口</a>
        </footer>
      </div>
      {selectedProject && <ProjectDetail project={selectedProject} onClose={() => setSelectedProject(null)} />}
    </>
  )
}
